import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { Check, Users, TrendingUp, Crown, Gift, Share2, Euro, Star, Loader2, X, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNER_PLANS = [
  {
    id: 'starter',
    name: 'Ambassador',
    subtitle: 'Inizia gratis',
    price: 0,
    period: 'gratis per sempre',
    icon: Gift,
    color: 'from-emerald-500 to-emerald-600',
    description: 'Perfetto per iniziare a guadagnare senza investimento',
    features: [
      { text: 'Link referral personale', included: true },
      { text: '5% commissione su ogni vendita', included: true, highlight: true },
      { text: 'Dashboard guadagni base', included: true },
      { text: 'Pagamenti mensili via PayPal', included: true },
      { text: 'Supporto via email', included: true },
      { text: 'Materiali marketing', included: false },
      { text: 'Link personalizzato', included: false },
    ],
    commission: 5,
    cta: 'Inizia Gratis',
    ctaStyle: 'bg-neutral-800 hover:bg-neutral-900',
  },
  {
    id: 'pro',
    name: 'Influencer',
    subtitle: 'Per chi fa sul serio',
    price: 19,
    period: '/mese',
    icon: TrendingUp,
    color: 'from-blue-500 to-blue-600',
    popular: true,
    description: 'Ideale per content creator e micro-influencer',
    features: [
      { text: 'Tutto di Ambassador +', included: true },
      { text: '10% commissione su ogni vendita', included: true, highlight: true },
      { text: 'Link personalizzato (tuonome.qantri.store)', included: true, highlight: true },
      { text: 'Kit materiali marketing premium', included: true },
      { text: 'Badge "Partner Verificato"', included: true },
      { text: 'Report analytics avanzati', included: true },
      { text: 'Supporto prioritario WhatsApp', included: true },
    ],
    commission: 10,
    cta: 'Diventa Influencer',
    ctaStyle: 'bg-[#064E3B] hover:bg-[#053d2f]',
  },
  {
    id: 'premium',
    name: 'Elite',
    subtitle: 'Per professionisti',
    price: 49,
    period: '/mese',
    icon: Crown,
    color: 'from-amber-500 to-amber-600',
    description: 'Massime commissioni per top performer',
    features: [
      { text: 'Tutto di Influencer +', included: true },
      { text: '15% commissione su ogni vendita', included: true, highlight: true },
      { text: 'Pagamenti settimanali', included: true, highlight: true },
      { text: 'Promozione sul nostro Instagram', included: true },
      { text: 'Accesso anticipato nuovi prodotti', included: true },
      { text: 'Account manager dedicato', included: true },
      { text: 'Formazione 1-to-1 mensile', included: true },
    ],
    commission: 15,
    cta: 'Diventa Elite',
    ctaStyle: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
  },
];

const BENEFITS = [
  {
    icon: Euro,
    title: 'Guadagni Reali',
    description: 'Fino al 15% su ogni vendita. Condividi, guadagna, ripeti.'
  },
  {
    icon: Share2,
    title: 'Facile da Condividere',
    description: 'Un link, mille possibilità. Social, WhatsApp, ovunque.'
  },
  {
    icon: Users,
    title: 'Nessun Limite',
    description: 'Più condividi, più guadagni. Senza tetti massimi.'
  },
  {
    icon: Star,
    title: 'Brand di Lusso',
    description: 'Promuovi prodotti di qualità che la gente vuole davvero.'
  },
];

const PricingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [newUserData, setNewUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    instagram: '',
    why: '',
  });
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithData } = useContext(UserContext);

  // Handle PayPal callback
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const paymentId = searchParams.get('paymentId');
    const PayerID = searchParams.get('PayerID');
    const registrationId = searchParams.get('reg_id');
    const planId = searchParams.get('plan');
    
    if (cancelled === 'true') {
      toast.error('Pagamento annullato');
      // Clean URL
      navigate('/pricing', { replace: true });
      return;
    }
    
    if (success === 'true' && paymentId && PayerID && registrationId) {
      // Confirm the payment
      confirmPayment(paymentId, PayerID, registrationId, planId);
    }
  }, [searchParams]);

  const confirmPayment = async (paymentId, PayerID, registrationId, planId) => {
    setConfirmingPayment(true);
    try {
      const res = await axios.post(`${API}/paypal/confirm-partner-subscription`, {
        paymentId,
        PayerID,
        registration_id: registrationId
      });
      
      if (res.data.success) {
        // Save token and login user
        localStorage.setItem('qantri_user_token', res.data.token);
        loginWithData(res.data.user);
        
        setNewUserData(res.data.user);
        setPaymentSuccess(true);
        toast.success('Benvenuto nel programma Partner QANTRI!');
        
        // Clean URL after a delay
        setTimeout(() => {
          navigate('/pricing', { replace: true });
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la conferma del pagamento');
      navigate('/pricing', { replace: true });
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowRegisterModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      if (selectedPlan.price === 0) {
        // Free plan - register directly
        const res = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          partner_plan: selectedPlan.id,
          instagram: formData.instagram,
        });
        
        // Auto-login the user
        localStorage.setItem('qantri_user_token', res.data.token);
        loginWithData(res.data.user);
        
        toast.success('Benvenuto! Il tuo link referral è pronto');
        setShowRegisterModal(false);
        // Redirect to referral page to see the link
        navigate('/referral');
      } else {
        // Paid plan - PayPal checkout
        const paymentRes = await axios.post(`${API}/paypal/create-subscription`, {
          plan_id: selectedPlan.id,
          plan_name: `Partner ${selectedPlan.name}`,
          amount: selectedPlan.price,
          description: `Abbonamento Partner ${selectedPlan.name} QANTRI`,
          user_email: formData.email,
          user_name: formData.name,
          user_password: formData.password,
          user_instagram: formData.instagram,
        });

        if (paymentRes.data.approval_url) {
          window.location.href = paymentRes.data.approval_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-300 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Sparkles className="h-4 w-4" />
              Programma Partner QANTRI
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Guadagna Online<br />con QANTRI
            </h1>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Condividi i nostri prodotti e guadagna commissioni su ogni vendita. 
              Nessun magazzino, nessun rischio. Inizi gratis in 1 minuto.
            </p>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-10">
              <div className="text-center">
                <p className="text-4xl font-bold">15%</p>
                <p className="text-emerald-200 text-sm">Commissione max</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold">€0</p>
                <p className="text-emerald-200 text-sm">Per iniziare</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold">24h</p>
                <p className="text-emerald-200 text-sm">Attivazione</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Come Funziona
          </h2>
          <p className="text-neutral-600 text-center mb-12 max-w-2xl mx-auto">
            Guadagnare con QANTRI è semplice. Nessuna esperienza richiesta.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '1', title: 'Iscriviti Gratis', desc: 'Crea il tuo account in 30 secondi e ottieni il tuo link personale' },
              { step: '2', title: 'Condividi', desc: 'Posta sui social, invia su WhatsApp, condividi con amici e follower' },
              { step: '3', title: 'Guadagna', desc: 'Ricevi commissioni su ogni acquisto fatto tramite il tuo link' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#064E3B] to-[#065F46] text-white text-2xl font-bold rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-neutral-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Perché Diventare Partner?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {BENEFITS.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-[#064E3B]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-[#064E3B]" />
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-neutral-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Earnings */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#064E3B] to-[#065F46] rounded-2xl p-8 md:p-12 text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quanto Puoi Guadagnare?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <p className="text-emerald-200 text-sm mb-2">10 vendite/mese</p>
                <p className="text-3xl font-bold">€50 - €150</p>
                <p className="text-emerald-200 text-xs mt-2">Hobby</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 transform md:scale-110">
                <p className="text-emerald-200 text-sm mb-2">50 vendite/mese</p>
                <p className="text-4xl font-bold">€250 - €750</p>
                <p className="text-emerald-200 text-xs mt-2">Side Hustle</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <p className="text-emerald-200 text-sm mb-2">200+ vendite/mese</p>
                <p className="text-3xl font-bold">€1000+</p>
                <p className="text-emerald-200 text-xs mt-2">Business</p>
              </div>
            </div>
            
            <p className="text-center text-emerald-200 text-sm mt-6">
              *Basato su ordine medio di €100 con commissione 5-15%
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-neutral-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Scegli il Tuo Piano
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Inizia gratis o sblocca commissioni più alte con i piani premium
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PARTNER_PLANS.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${plan.popular ? 'ring-2 ring-[#064E3B] md:scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-[#064E3B] text-white text-center py-2 text-sm font-medium">
                    Più Scelto
                  </div>
                )}
                
                <div className={`p-6 ${plan.popular ? 'pt-12' : ''}`}>
                  {/* Header */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                    <plan.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                  <p className="text-neutral-500 text-sm mb-2">{plan.subtitle}</p>
                  <p className="text-neutral-600 text-sm mb-4">{plan.description}</p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    {plan.price === 0 ? (
                      <>
                        <span className="text-4xl font-bold">Gratis</span>
                        <span className="text-neutral-500 text-sm block">{plan.period}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">€{plan.price}</span>
                        <span className="text-neutral-500">{plan.period}</span>
                      </>
                    )}
                  </div>

                  {/* Commission highlight */}
                  <div className="bg-emerald-50 text-emerald-800 rounded-lg p-3 mb-6 text-center">
                    <span className="text-2xl font-bold">{plan.commission}%</span>
                    <span className="text-sm block">su ogni vendita</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className={`h-5 w-5 flex-shrink-0 ${feature.highlight ? 'text-emerald-500' : 'text-neutral-400'}`} />
                        ) : (
                          <X className="h-5 w-5 flex-shrink-0 text-neutral-300" />
                        )}
                        <span className={`text-sm ${feature.included ? (feature.highlight ? 'text-emerald-700 font-medium' : 'text-neutral-700') : 'text-neutral-400'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full ${plan.ctaStyle}`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {plan.cta}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Domande Frequenti
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'Devo pagare per iniziare?',
                a: 'No! Il piano Ambassador è completamente gratuito. Puoi iniziare a guadagnare subito senza nessun investimento.'
              },
              {
                q: 'Come ricevo i pagamenti?',
                a: 'I pagamenti vengono inviati via PayPal ogni mese (o ogni settimana per il piano Elite). Minimo di prelievo: €20.'
              },
              {
                q: 'Devo avere tanti follower?',
                a: 'No! Anche condividere con amici e familiari funziona. Molti partner guadagnano con meno di 1000 follower.'
              },
              {
                q: 'Posso cambiare piano?',
                a: 'Sì, puoi fare upgrade in qualsiasi momento e iniziare subito a guadagnare commissioni più alte.'
              },
              {
                q: 'Quanto tempo ci vuole per iniziare?',
                a: 'Meno di 2 minuti! Registrati, ricevi il tuo link e inizia subito a condividere.'
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="border-b border-neutral-200 pb-6"
              >
                <h3 className="font-semibold text-lg mb-2">{faq.q}</h3>
                <p className="text-neutral-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#064E3B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Pronto a Guadagnare?
          </h2>
          <p className="text-emerald-200 mb-8 max-w-xl mx-auto">
            Unisciti ora e inizia a guadagnare commissioni su ogni vendita. È gratis!
          </p>
          <Button 
            onClick={() => handleSelectPlan(PARTNER_PLANS[0])}
            className="bg-white text-[#064E3B] hover:bg-neutral-100 text-lg px-8 py-6"
          >
            Inizia Gratis Ora
          </Button>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegisterModal && selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold">Diventa {selectedPlan.name}</h3>
                  <p className="text-sm text-neutral-500">
                    {selectedPlan.price === 0 ? 'Gratis per sempre' : `€${selectedPlan.price}/mese`} • {selectedPlan.commission}% commissioni
                  </p>
                </div>
                <button 
                  onClick={() => setShowRegisterModal(false)}
                  className="p-2 hover:bg-neutral-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome completo *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Mario Rossi"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="mario@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Minimo 8 caratteri"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Instagram (opzionale)</label>
                  <Input
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    placeholder="@tuousername"
                  />
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    <strong>Cosa ricevi:</strong><br />
                    ✓ Link referral personale<br />
                    ✓ {selectedPlan.commission}% su ogni vendita<br />
                    ✓ Dashboard per tracciare i guadagni
                  </p>
                </div>

                <Button
                  type="submit"
                  className={`w-full ${selectedPlan.ctaStyle}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    selectedPlan.price === 0 ? 'Attiva Gratis' : `Procedi al Pagamento - €${selectedPlan.price}`
                  )}
                </Button>

                <p className="text-xs text-center text-neutral-500">
                  Cliccando accetti i Termini di Servizio Partner
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Payment Confirmation Loading */}
      {confirmingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4"
          >
            <Loader2 className="h-12 w-12 animate-spin text-[#064E3B] mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Conferma in corso...</h3>
            <p className="text-neutral-600">
              Stiamo verificando il tuo pagamento e creando il tuo account Partner.
            </p>
          </motion.div>
        </div>
      )}

      {/* Payment Success Modal */}
      {paymentSuccess && newUserData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 text-center max-w-md mx-4"
          >
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Benvenuto, {newUserData.name}!</h3>
            <p className="text-neutral-600 mb-6">
              Sei ufficialmente un Partner QANTRI {newUserData.partner_plan === 'pro' ? 'Influencer' : 'Elite'}!
            </p>
            
            <div className="bg-emerald-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-emerald-800 mb-2"><strong>Il tuo codice referral:</strong></p>
              <p className="text-2xl font-bold text-emerald-700 font-mono">{newUserData.referral_code}</p>
              <p className="text-sm text-emerald-600 mt-2">
                Commissione: <strong>{newUserData.partner_commission}%</strong> su ogni vendita
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={() => navigate('/referral')}
                className="flex-1 bg-[#064E3B] hover:bg-[#053d2f]"
              >
                Vai alla Dashboard
              </Button>
              <Button 
                onClick={() => setPaymentSuccess(false)}
                variant="outline"
                className="flex-1"
              >
                Chiudi
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
